import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import EN from "../data/en.json";
import AR from "../data/ar.json";
import { Footer } from "../components/footer";
import { EnterpriseNavbar } from "../components/enterprise/EnterpriseNavbar";
import PlatformMediaGallery from "../components/enterprise/PlatformMediaGallery";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { mergeEnterpriseCaseStudyFarms } from "../data/enterpriseCaseStudyContent";
import { buildPublicFarmPreviewUrl, isFarmPreviewEnabled } from "../utils/publicFarmPreview";
import PublicFarmPreview from "../components/enterprise/PublicFarmPreview";

const CROP_SLUGS = ["cotton", "wheat", "tomato", "citrus", "sugarcane"];
const cropSlugFromText = (text = "") => {
  const t = String(text).toLowerCase();
  return CROP_SLUGS.find((slug) => t.includes(slug)) || null;
};

const FadeIn = ({ children, delay = 0, className = "", direction = "up" }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-40px 0px" });
  const yOff = direction === "up" ? 40 : direction === "down" ? -40 : 0;
  const xOff = direction === "left" ? 40 : direction === "right" ? -40 : 0;
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: yOff, x: xOff }} animate={inView ? { opacity: 1, y: 0, x: 0 } : {}} transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const translateMetricLabel = (label, isArabic) => {
  if (!isArabic) return label;
  const dictionary = {
    Area: "المساحة",
    Elevation: "الارتفاع",
    "Remote Supply Chain": "بعد سلاسل الإمداد",
    "WUE Focus": "تركيز كفاءة المياه",
    Solar: "الطاقة الشمسية",
    "Well Depth": "عمق البئر",
    "Water Use Efficiency": "كفاءة استخدام المياه",
    Year: "السنة",
    Cows: "الأبقار",
    Sheep: "الأغنام",
    Model: "النموذج",
    "Crop Focus": "المحصول المستهدف",
    "Lead Variety": "الصنف الأبرز",
    "Heat-wave Adaptation": "التكيف مع الموجات الحارة",
    "Study Type": "نوع الدراسة",
    "Capacity Stretch": "تمديد السعة",
    "Client Type": "نوع العميل",
    Status: "الحالة",
    "Heat-wave Yield Advantage": "ميزة الإنتاجية أثناء الحر",
    "Varieties Tested": "الأصناف المختبرة",
    "Farm Area": "مساحة المزرعة",
    Cattle: "الماشية",
    "Irrigation Network": "شبكة الري",
    WUE: "كفاءة المياه",
    Yield: "الإنتاجية",
  };
  return dictionary[label] || label;
};

const translateSummaryLabel = (label, isArabic) => {
  if (!isArabic) return label;
  const dictionary = {
    // Category headers
    Infrastructure: "البنية التحتية",
    Production: "الإنتاج",
    Efficiency: "الكفاءة",
    Research: "الأبحاث",
    Livestock: "الثروة الحيوانية",
    // Shared labels
    "Irrigation Network": "شبكة الري",
    "Water Use Efficiency": "كفاءة استخدام المياه",
    "Water Saved": "توفير المياه",
    "Irrigation Type": "نوع الري",
    "NDVI Gain": "تحسّن NDVI",
    "Farm Area": "مساحة المزرعة",
    // Toshka
    "Well Depth": "عمق البئر",
    "Pumping Source": "مصدر الضخ",
    "Solar Capacity": "الطاقة الشمسية",
    "Supply Chain Run": "مسافة سلسلة الإمداد",
    "Elevation Range": "نطاق الارتفاع",
    "Total Yield (Season 4)": "إجمالي الإنتاج (الموسم ٤)",
    "Wheat Yield (Season 3)": "إنتاج القمح (الموسم ٣)",
    "Barley Yield (Season 3)": "إنتاج الشعير (الموسم ٣)",
    "Clover Cycles": "دورات البرسيم",
    "Capacity Stretch": "تمديد السعة",
    "Remote Supply Run": "مسافة سلسلة الإمداد",
    // Farafra
    "Total Land": "إجمالي الأرض",
    "Well Count": "عدد الآبار",
    "Crop Mix": "تنوع المحاصيل",
    "Annual Yield": "الإنتاج السنوي",
    "Primary Crops": "المحاصيل الرئيسية",
    "Secondary Crops": "المحاصيل الثانوية",
    "Season 3 Wheat Yield": "إنتاج الموسم الثالث - قمح",
    "Season 3 Barley Yield": "إنتاج الموسم الثالث - شعير",
    // Fayoum
    "Cattle": "الماشية",
    "Sheep": "الأغنام",
    "Annual Yield (Crops)": "الإنتاج السنوي (محاصيل)",
    // Cotton
    "Research Partner": "شريك البحث",
    "Study Type": "نوع الدراسة",
    "Trial Design": "تصميم التجربة",
    "Varieties Tested": "الأصناف المختبرة",
    "Lead Variety": "الصنف الأبرز",
    "Total Yield": "إجمالي الإنتاج",
    "Giza 98 Yield": "إنتاج جيزة ٩٨",
    "Giza 94 Yield": "إنتاج جيزة ٩٤",
    "Giza 95 Yield": "إنتاج جيزة ٩٥",
    "Giza 97 Yield": "إنتاج جيزة ٩٧",
    "Heat-wave Yield Advantage": "ميزة الإنتاجية أثناء الحر",
    // Toshka additions
    "Irrigation System": "نظام الري",
    "Elevation": "الارتفاع",
    "Crops": "المحاصيل",
    "Cumulative Yield": "إجمالي الإنتاج التراكمي",
    "Operating Years": "سنوات التشغيل",
    "Cost Trend": "اتجاه التكلفة",
    "Revenue Trend": "اتجاه الإيرادات",
    // Farafra additions
    "Wells": "الآبار",
    "Restoration Method": "طريقة الاستصلاح",
    // Fayoum additions
    "System Model": "نموذج النظام",
    "Fodder (Berseem)": "أعلاف (برسيم)",
    "Wheat (Winter)": "قمح (شتوي)",
    "Rotational Pasture": "مرعى دوري",
    // Minya
    "Well Drilling": "حفر الآبار",
    "Service Model": "نموذج الخدمة",
    "Partnership Type": "نوع الشراكة",
    "Region": "المنطقة",
    "Services Delivered": "الخدمات المقدمة",
    "Platform Modules": "وحدات المنصة",
    Model: "النموذج",
  };
  return dictionary[label] || label;
};

const SUMMARY_GROUP_META = {
  Infrastructure: {
    accent: "from-slate-900 via-slate-800 to-emerald-900",
    badge: "bg-slate-900 text-white",
    ring: "ring-slate-200/80",
  },
  Production: {
    accent: "from-amber-600 via-amber-500 to-emerald-500",
    badge: "bg-amber-100 text-amber-900",
    ring: "ring-amber-200/70",
  },
  Efficiency: {
    accent: "from-emerald-700 via-emerald-600 to-cyan-600",
    badge: "bg-emerald-100 text-emerald-900",
    ring: "ring-emerald-200/70",
  },
  Research: {
    accent: "from-fuchsia-700 via-violet-700 to-slate-900",
    badge: "bg-violet-100 text-violet-900",
    ring: "ring-violet-200/70",
  },
  Livestock: {
    accent: "from-rose-700 via-amber-700 to-slate-900",
    badge: "bg-rose-100 text-rose-900",
    ring: "ring-rose-200/70",
  },
  Services: {
    accent: "from-sky-700 via-cyan-700 to-emerald-700",
    badge: "bg-sky-100 text-sky-900",
    ring: "ring-sky-200/70",
  },
  Overview: {
    accent: "from-slate-900 via-slate-800 to-emerald-900",
    badge: "bg-slate-900 text-white",
    ring: "ring-slate-200/80",
  },
};

const groupSummaryRows = (rows = []) => {
  const groups = new Map();

  rows.forEach((row) => {
    const category = row.category || "Overview";
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category).push(row);
  });

  return Array.from(groups.entries()).map(([category, items]) => ({
    category,
    items,
  }));
};

const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="fixed top-0 left-0 z-[100] h-1 bg-emerald-500 transition-[width] duration-100" style={{ width: `${progress}%` }} />;
};

export const CaseStudyPage = ({ lang, toggleLanguage }) => {
  const { farmId } = useParams();
  const isArabic = lang === "AR";
  const data = isArabic ? AR : EN;
  const enterprise = data.Enterprise;
  const navItems = [
    { label: isArabic ? "نظرة عامة" : "Overview", url: "#enterprise-hero" },
    { label: isArabic ? "المنصة" : "Platform", url: "#platform" },
    { label: isArabic ? "سجل الإنجاز" : "Track Record", url: "#track-record" },
    { label: isArabic ? "النموذج" : "Model", url: "#operating-model" },
    { label: isArabic ? "المحاصيل" : "Crops", url: "/crops" },
    { label: isArabic ? "تواصل" : "Inquiry", url: "#inquiry" },
  ];
  const cs = enterprise.caseStudies;
  const caseStudyUi = cs.ui || {};
  const farms = mergeEnterpriseCaseStudyFarms(cs.farms);
  const farm = farms.find(f => f.id === farmId);
  const summaryGroups = groupSummaryRows(farm?.summaryTable || []);

  useEffect(() => { window.scrollTo(0, 0); }, [farmId]);

  if (!farm) {
    return (
      <div dir={isArabic ? "rtl" : "ltr"} className="min-h-screen bg-[#f5faf5]">
        <EnterpriseNavbar lang={lang} toggleLanguage={toggleLanguage} navItems={navItems} actions={enterprise.navCtas} />
        <div className="flex items-center justify-center py-40">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">{caseStudyUi.projectNotFound || (isArabic ? "المشروع غير موجود" : "Project not found")}</h2>
            <Link to="/#track-record" className="text-emerald-600 font-semibold no-underline">{cs.backToProjects} →</Link>
          </div>
        </div>
        <Footer lang={lang} />
      </div>
    );
  }

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="min-h-screen bg-[#f5faf5] text-slate-900 font-sans selection:bg-emerald-200">
      <EnterpriseNavbar lang={lang} toggleLanguage={toggleLanguage} navItems={navItems} actions={enterprise.navCtas} />

      {/* READING PROGRESS BAR */}
      <ScrollProgress />

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/40 to-transparent" />
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 30px, rgba(255,255,255,0.03) 30px, rgba(255,255,255,0.03) 31px)" }} />
          <div className="relative mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] px-4 py-16 md:px-6 md:py-24 3xl:px-10 3xl:py-32 4k:px-16 4k:py-40">
            <FadeIn>
              <Link to="/#track-record" className="inline-flex items-center gap-2 text-emerald-300 text-sm font-semibold no-underline hover:text-emerald-200 transition mb-8">
                ← {cs.backToProjects}
              </Link>
              <div className="grid gap-10 lg:grid-cols-[1.3fr_0.7fr] lg:items-end">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.3em] text-emerald-400 mb-4">{farm.region}</p>
                  <h1 className="text-5xl md:text-7xl 3xl:text-8xl 4k:text-9xl 5k:text-[10rem] font-bold leading-[1.05] mb-4">{farm.name}</h1>
                  {farm.tagline && (
                    <p className="text-lg md:text-xl 3xl:text-2xl 4k:text-3xl 5k:text-4xl italic text-emerald-300 font-medium mb-6 max-w-2xl 3xl:max-w-4xl 4k:max-w-5xl 5k:max-w-7xl">{farm.tagline}</p>
                  )}
                  <p className="text-xl 3xl:text-2xl 4k:text-3xl 5k:text-4xl text-slate-300 font-medium leading-relaxed max-w-2xl 3xl:max-w-4xl 4k:max-w-5xl 5k:max-w-7xl">{farm.longDescription || farm.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 4k:gap-6 5k:gap-8">
                  {(farm.metrics || [
                    { label: isArabic ? "المساحة" : "Area", value: farm.size },
                    { label: isArabic ? "طاقة شمسية" : "Solar", value: farm.solar },
                    { label: isArabic ? "الإحداثيات" : "Coords", value: farm.coords },
                    { label: isArabic ? "المحاصيل" : "Crops", value: farm.crops },
                  ]).map(stat => {
                    const cropSlug = cropSlugFromText(typeof stat.value === "string" ? stat.value : "");
                    return (
                    <div key={stat.label} className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur">
                      <p className="text-xs 4k:text-sm 5k:text-base font-bold uppercase tracking-[0.2em] text-emerald-400 mb-1">{translateMetricLabel(stat.label, isArabic)}</p>
                      <p className="text-lg 3xl:text-xl 4k:text-3xl 5k:text-4xl font-bold text-white mb-0">{stat.value}</p>
                      {cropSlug && (
                        <Link to={`/crops/${cropSlug}`} className="mt-1 inline-block text-[11px] font-semibold text-emerald-400 no-underline hover:text-emerald-300">
                          {isArabic ? "دليل المحصول ←" : "Crop guide →"}
                        </Link>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* THE CHALLENGE — continuation of hero */}
        {farm.mainChallenge && (
          <section className="relative overflow-hidden bg-slate-950 text-white -mt-px">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950 to-slate-950" />
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 30px, rgba(255,255,255,0.03) 30px, rgba(255,255,255,0.03) 31px)" }} />
            <div className="relative mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] 5k:max-w-[2800px] px-4 pb-16 pt-8 md:px-6 md:pb-24 md:pt-12 3xl:px-10 3xl:pb-32 3xl:pt-16 4k:px-16 4k:pb-40 4k:pt-20 5k:px-24 5k:pb-48 5k:pt-24">
              <FadeIn>
                <div className={`flex items-start gap-4 mb-6 ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/20 flex items-center justify-center mt-0.5">
                    <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" /></svg>
                  </div>
                  <div>
                    <p className="text-xs 4k:text-sm 5k:text-base font-bold uppercase tracking-[0.24em] text-amber-400 mb-1.5">
                      {cs.challengeHeading || (isArabic ? "التحدي" : "The Challenge")}
                    </p>
                    <p className="text-lg md:text-xl 4k:text-2xl 5k:text-3xl text-slate-300 font-medium leading-relaxed mb-0 max-w-3xl 4k:max-w-5xl 5k:max-w-7xl">{farm.mainChallenge}</p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </section>
        )}

        {/* SUMMARY — Numbers That Matter (SaaS Dashboard Bento Box) */}
        {farm.summaryTable && farm.summaryTable.length > 0 && (() => {
          return (
            <section className="bg-[#f5faf5] py-16 md:py-24 3xl:py-32">
              <div className="mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] 5k:max-w-[2800px] px-4 md:px-6 3xl:px-10 4k:px-16 5k:px-24">
                <div className={`flex flex-col lg:flex-row gap-12 lg:gap-20 ${isArabic ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Left Column: Sticky Title */}
                  <div className="lg:w-1/3">
                    <div className="sticky top-32">
                      <FadeIn>
                        <div className={`inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-emerald-100/50 border border-emerald-200/50 ${isArabic ? 'flex-row-reverse' : ''}`}>
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                            {caseStudyUi.performanceSummaryEyebrow || (isArabic ? "ملخص الأداء" : "Performance Summary")}
                          </span>
                        </div>
                        <h2 className="text-4xl md:text-5xl 3xl:text-6xl 4k:text-7xl 5k:text-8xl font-bold text-slate-900 mb-6 leading-[1.1]">
                          {caseStudyUi.numbersTitle || (isArabic ? "الأرقام التي تهم" : "Numbers That Matter")}
                        </h2>
                        <p className="text-lg 3xl:text-xl 4k:text-2xl 5k:text-3xl text-slate-600 font-medium leading-relaxed">
                          {caseStudyUi.numbersParagraph || (isArabic 
                            ? "نظرة عامة على حجم المزرعة، والإنتاجية، وتحسينات الكفاءة التي تم تحقيقها من خلال الاعتماد على تقنيات منصة فارمتوبيا."
                            : "A high-level overview of the farm's scale, yield, and efficiency improvements achieved through the Farmtopia platform.")}
                        </p>
                      </FadeIn>
                    </div>
                  </div>

                  {/* Right Column: Bento Grid */}
                  <div className="lg:w-2/3">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 md:gap-6 4k:gap-8">
                      {summaryGroups.map((group, i) => {
                        const groupMeta =
                          SUMMARY_GROUP_META[group.category] || SUMMARY_GROUP_META.Overview;

                        return (
                          <FadeIn key={group.category} delay={i * 0.08}>
                            <div className={`overflow-hidden rounded-[2rem] bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)] ring-1 ${groupMeta.ring}`}>
                              <div className={`bg-gradient-to-r ${groupMeta.accent} px-6 py-5 md:px-7`}>
                                <div className={`flex items-end justify-between gap-4 ${isArabic ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                  <div>
                                    <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] ${groupMeta.badge}`}>
                                      {translateSummaryLabel(group.category, isArabic)}
                                    </span>
                                    <h3 className="mt-3 text-2xl font-bold text-white md:text-3xl">
                                      {group.items[0]?.value}
                                    </h3>
                                    <p className="mt-1 text-sm font-medium text-white/75">
                                      {translateSummaryLabel(group.items[0]?.label || group.category, isArabic)}
                                    </p>
                                  </div>
                                  <p className="mb-0 text-xs font-mono uppercase tracking-[0.2em] text-white/55">
                                    {group.items.length} {caseStudyUi.metricsCountLabel || (isArabic ? "مؤشر" : "metrics")}
                                  </p>
                                </div>
                              </div>

                              <div className="grid gap-0">
                                {group.items.map((row, rowIndex) => (
                                  <div
                                    key={`${group.category}-${row.label}`}
                                    className={`grid gap-2 px-6 py-4 md:grid-cols-[0.9fr_1.1fr] md:px-7 ${
                                      rowIndex === 0 ? "" : "border-t border-slate-100"
                                    } ${isArabic ? "md:text-right" : "md:text-left"}`}
                                  >
                                    <p className="mb-0 text-sm font-semibold text-slate-500">
                                      {translateSummaryLabel(row.label, isArabic)}
                                    </p>
                                    <p
                                      className={`mb-0 font-bold tracking-tight text-slate-900 ${
                                        row.value.length > 26
                                          ? "text-base md:text-lg"
                                          : row.value.length > 16
                                            ? "text-xl md:text-2xl"
                                            : "text-2xl md:text-3xl"
                                      }`}
                                    >
                                      {row.value}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </FadeIn>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })()}

        {/* FARM PREVIEW — Interactive Data Visualizations */}
        {farm.preview && farm.publicPreviewId && !farm.lightCase && (
          <section className="mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] 5k:max-w-[2800px] px-4 py-16 md:px-6 md:py-24 3xl:px-10 3xl:py-32 4k:px-16 4k:py-40 5k:px-24 5k:py-48">
            <FadeIn>
              <p className="text-sm 5k:text-base font-bold uppercase tracking-[0.24em] text-emerald-600 mb-3">
                {caseStudyUi.farmPreviewEyebrow || (isArabic ? "معاينة المزرعة — منصة فارمتوبيا" : "Farm Preview — Farmtopia Platform")}
              </p>
              <h2 className="text-3xl md:text-5xl 3xl:text-6xl 4k:text-7xl 5k:text-8xl font-bold text-slate-900 mb-4">
                {caseStudyUi.farmPreviewTitle || (isArabic ? "من الصحراء إلى الأخضر" : "From Desert to Green")}
              </h2>
              <p className="text-lg 3xl:text-xl 4k:text-2xl 5k:text-3xl text-slate-600 font-medium mb-10 max-w-3xl 3xl:max-w-4xl 4k:max-w-5xl 5k:max-w-7xl">
                {caseStudyUi.farmPreviewParagraph || (isArabic
                  ? "استعرض نسخة تفاعلية من المشروع: خرائط، طبقات أقمار صناعية، محطات تاريخية، ومؤشرات الأداء الأساسية داخل نفس موقع العرض."
                  : "Explore an interactive project view with map layers, historical milestones, and core operating metrics directly inside the same site.")}
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              {isFarmPreviewEnabled() ? (
                <div className="relative w-full overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-xl" style={{ minHeight: 700 }}>
                  <iframe
                    data-testid="farm-preview-iframe"
                    src={buildPublicFarmPreviewUrl(farm.publicPreviewId)}
                    title={`${farm.name} — Farmtopia Platform Preview`}
                    className="w-full border-0"
                    style={{ height: 700 }}
                    loading="eager"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock"
                  />
                </div>
              ) : (
                // LR-1: until the live V2 platform preview is reachable
                // (REACT_APP_ENABLE_FARM_PREVIEW=1 + the alpha V2 host), render the
                // self-contained PublicFarmPreview — the animated satellite / NDVI
                // dashboard with widgets and copy — rather than embedding a 404.
                <PublicFarmPreview farm={farm} isArabic={isArabic} />
              )}
            </FadeIn>
          </section>
        )}

        {/* PLATFORM FEATURES — Real screenshot resources */}
        {farm.platformFeatures && farm.platformFeatures.length > 0 && (
          <section className="bg-slate-950 text-white overflow-hidden relative">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(16,185,129,0.15),_transparent_60%)]" />
            <div className="relative mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] 5k:max-w-[2800px] px-4 py-16 md:px-6 md:py-24 3xl:px-10 3xl:py-32 4k:px-16 4k:py-40 5k:px-24 5k:py-48">
              <FadeIn>
                <p className="text-sm 5k:text-base font-bold uppercase tracking-[0.24em] text-emerald-400 mb-3">
                  {caseStudyUi.technologyEyebrow || (isArabic ? "منصة فارمتوبيا في هذا المشروع" : "Farmtopia Platform on This Project")}
                </p>
                <h2 className="text-3xl md:text-5xl 3xl:text-6xl 4k:text-7xl 5k:text-8xl font-bold mb-4">
                  {caseStudyUi.technologyTitle || (isArabic ? "طبقة التكنولوجيا" : "Technology Layer")}
                </h2>
                <p className="text-lg 3xl:text-xl 4k:text-2xl 5k:text-3xl text-slate-400 font-medium mb-8 max-w-3xl 3xl:max-w-4xl 4k:max-w-5xl 5k:max-w-7xl">
                  {caseStudyUi.technologyParagraph || (isArabic
                    ? "نعرض هنا نفس اللقطات والموارد المرئية القادمة من صفحة المنصة، لكن داخل سياق هذا المشروع تحديدًا حتى يرتبط المنتج مباشرة بالأثر الميداني."
                    : "Here we reuse the same real screenshots and media resources from the platform landing page, but framed around this specific project so the product is directly tied to field impact.")}
                </p>
              </FadeIn>

              <FadeIn delay={0.2}>
                <div className="grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
                  <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur md:p-8">
                    <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-emerald-400">
                      {caseStudyUi.operatingLayerEyebrow || (isArabic ? "طبقة التشغيل" : "Operating Layer")}
                    </p>
                    <h3 className="mb-4 text-2xl font-bold text-white md:text-3xl">
                      {caseStudyUi.operatingLayerTitle || (isArabic ? "الأنظمة التي كانت تدير هذا المشروع" : "The systems running this project")}
                    </h3>
                    <p className="mb-6 text-sm leading-relaxed text-slate-300 md:text-base">
                      {caseStudyUi.operatingLayerParagraph || (isArabic
                        ? "كل واجهة هنا تمثل جزءًا تشغيليًا حقيقيًا داخل المشروع — من المتابعة الميدانية والري إلى التحليل بالأقمار الصناعية والتخطيط التنفيذي."
                        : "Each surface below reflects a live operational layer on this project — from field monitoring and irrigation control to satellite analysis and execution planning.")}
                    </p>

                    <div className="space-y-3">
                      {farm.platformFeatures.map((feat, i) => (
                        <div
                          key={feat}
                          className="rounded-[1.25rem] border border-white/10 bg-slate-900/50 px-4 py-3"
                        >
                          <div className={`flex items-start gap-3 ${isArabic ? "flex-row-reverse text-right" : "text-left"}`}>
                            <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-xs font-bold text-emerald-300 ring-1 ring-emerald-500/20">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <p className="mb-0 text-sm font-semibold leading-relaxed text-white/95">
                              {feat}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-[2rem] border border-white/10 bg-white/[0.03] p-4 md:p-6">
                    <PlatformMediaGallery
                      isArabic={isArabic}
                      indices={farm.technologyIndices || [0, 1, 2, 4]}
                      layout="grid"
                      showIntro={false}
                    />
                  </div>
                </div>
              </FadeIn>

              <FadeIn delay={0.6}>
                <div className="mt-10 flex flex-wrap gap-4 justify-center">
                  <Link
                    to="/platform"
                    className="rounded-full bg-emerald-500 px-10 py-4 text-base font-bold text-white no-underline hover:bg-emerald-400 transition-all shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:-translate-y-1"
                  >
                    {caseStudyUi.explorePlatform || (isArabic ? "استكشف المنصة" : "Explore the Platform")}
                  </Link>
                  <a href="/#inquiry" className="rounded-full border-2 border-slate-600 px-10 py-4 text-base font-bold text-white no-underline hover:border-slate-400 transition-colors">
                    {caseStudyUi.exploreAllFeatures || (isArabic ? "استكشف كل المزايا" : "Explore All Features")}
                  </a>
                </div>
              </FadeIn>
            </div>
          </section>
        )}

        {/* THE JOURNEY — Timeline milestones */}
        {farm.journey && farm.journey.length > 0 && (
          <section className="mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] 5k:max-w-[2800px] px-4 py-16 md:px-6 md:py-20 3xl:px-10 3xl:py-28 4k:px-16 4k:py-36 5k:px-24 5k:py-48">
            <FadeIn>
              <p className="text-sm 5k:text-base font-bold uppercase tracking-[0.24em] text-emerald-600 mb-3">
                {caseStudyUi.journeyEyebrow || (isArabic ? "الرحلة" : "The Journey")}
              </p>
              <h2 className="text-3xl md:text-4xl 3xl:text-5xl 4k:text-6xl 5k:text-7xl font-bold text-slate-900 mb-12">
                {caseStudyUi.journeyTitle || (isArabic ? "رحلة التحول" : "From Ground Zero")}
              </h2>
            </FadeIn>
            <div className="relative">
              <div className={`absolute top-0 bottom-0 w-px bg-emerald-200 ${isArabic ? 'right-6 md:right-1/2' : 'left-6 md:left-1/2'}`} />
              <div className="space-y-12">
                {farm.journey.map((entry, i) => (
                  <FadeIn key={entry.year + entry.phase} delay={i * 0.15} direction={!isArabic && i % 2 === 0 ? "right" : "left"}>
                    <div className={`relative flex items-start gap-6 md:gap-12 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} ${isArabic ? 'flex-row-reverse' : ''}`}>
                      <div className={`absolute ${isArabic ? 'right-3.5 md:right-[calc(50%-6px)]' : 'left-3.5 md:left-[calc(50%-6px)]'} w-5 h-5 rounded-full bg-emerald-500 border-4 border-white shadow-md z-10`} />
                      <div className={`${isArabic ? 'mr-14 md:mr-0' : 'ml-14 md:ml-0'} md:w-[calc(50%-2rem)] ${i % 2 === 0 ? '' : 'md:text-right'} ${isArabic && i % 2 === 0 ? 'md:text-right' : ''} ${isArabic && i % 2 !== 0 ? 'md:text-left' : ''}`}>
                        <div className="rounded-2xl border border-emerald-100 bg-[#f8fcf8] p-6 shadow-sm hover:shadow-lg transition-shadow">
                          <span className="inline-block text-sm font-bold font-mono text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full mb-3">{entry.year}</span>
                          <h3 className="text-xl font-bold text-slate-900 mb-2">{entry.phase}</h3>
                          <p className="text-base text-slate-600 leading-relaxed mb-0">{entry.detail}</p>
                        </div>
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* REGISTER YOUR FARM CTA */}
        <section className="mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] 5k:max-w-[2800px] px-4 py-16 md:px-6 md:py-24 3xl:px-10 3xl:py-32 4k:px-16 4k:py-40 5k:px-24 5k:py-48">
          <FadeIn>
            <div className="rounded-[2.5rem] bg-gradient-to-br from-emerald-600 to-emerald-800 p-10 md:p-16 4k:p-24 5k:p-32 text-white relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-96 h-96 4k:w-[800px] 4k:h-[800px] bg-emerald-500 rounded-full blur-[100px] 4k:blur-[200px] opacity-30 translate-x-1/3 -translate-y-1/3" />
              <div className="relative z-10 grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
                <div>
                  <h2 className="text-3xl md:text-5xl 3xl:text-6xl 4k:text-7xl 5k:text-8xl font-bold mb-6">
                    {caseStudyUi.registerTitle || (isArabic ? "هل لديك أرض تريد تطويرها؟" : "Have Land You Want to Develop?")}
                  </h2>
                  <p className="text-lg 3xl:text-xl 4k:text-2xl 5k:text-3xl text-emerald-50 font-medium leading-relaxed mb-8 max-w-xl 4k:max-w-3xl 5k:max-w-5xl">
                    {caseStudyUi.registerParagraph || (isArabic
                      ? "سجّل مزرعتك بالإحداثيات وستتلقى تقييمًا أوليًا مجانيًا مع تحليل NDVI ونموذج محصولي مبدئي عبر منصة فارمتوبيا."
                      : "Register your farm with coordinates and receive a free preliminary assessment with NDVI analysis and an initial crop model via the Farmtopia Platform.")}
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a href="/#inquiry" className="rounded-full bg-white px-8 py-4 text-base font-bold text-emerald-700 no-underline hover:bg-slate-50 transition-colors">
                      {caseStudyUi.registerPrimaryCta || (isArabic ? "سجّل مزرعتك" : "Register Your Farm")}
                    </a>
                    <Link to="/#track-record" className="rounded-full border-2 border-white/30 px-8 py-4 text-base font-bold text-white no-underline hover:border-white/60 transition-colors">
                      {cs.backToProjects}
                    </Link>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: "📍", ...(caseStudyUi.registerCards?.[0] || { title: isArabic ? "إحداثيات GPS" : "GPS Coordinates", desc: isArabic ? "حدد موقع أرضك بدقة" : "Pin your land precisely" }) },
                    { icon: "🛰️", ...(caseStudyUi.registerCards?.[1] || { title: isArabic ? "تحليل أقمار صناعية" : "Satellite Analysis", desc: isArabic ? "تقييم NDVI فوري" : "Instant NDVI assessment" }) },
                    { icon: "🌾", ...(caseStudyUi.registerCards?.[2] || { title: isArabic ? "نموذج محصولي" : "Crop Model", desc: isArabic ? "توصيات أولية" : "Initial recommendations" }) },
                    { icon: "📊", ...(caseStudyUi.registerCards?.[3] || { title: isArabic ? "تقرير مجاني" : "Free Report", desc: isArabic ? "تقرير جدوى أولي" : "Preliminary feasibility" }) },
                  ].map(item => (
                    <div key={item.title} className="rounded-2xl bg-white/10 backdrop-blur border border-white/10 p-4">
                      <span className="text-2xl">{item.icon}</span>
                      <p className="text-sm font-bold text-white mt-2 mb-1">{item.title}</p>
                      <p className="text-xs text-emerald-100 mb-0">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </section>
      </main>

      <Footer lang={lang} />
    </div>
  );
};

export default CaseStudyPage;
